import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Grid,
  FormControl,
  Typography,
  TextField,
  Button,
  MenuItem,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Check } from "@material-ui/icons";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { commonInlineStyles } from "../../styles";

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 800,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "& td, & th": {
      borderRight: "1px solid #e0e0e0",
    },
    "& td:last-child, & th:last-child": {
      borderRight: "none",
    },
  },
  tableHead: {
    backgroundColor: "#f9f9fc",
  },
  tableCell: {
    fontSize: 16,
  },
  formField: {
    width: "100%",
    marginBottom: 0,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },
  mandatoryDropdown: {
    width: "100%",
    "& .MuiSelect-root": {
      fontSize: "14px",
      padding: "8px 12px",
      textAlign: "left",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#c0c0c0",
    },
  },
}));

// Removed hardcoded ingestionDataElements - will be passed as prop from API

const ingestionCategoryOptions = ["PO Collaboration", "Inventory"];
const ingestThroughOptions = [
  { value: "Excel (Via Email)", disabled: false },
  { value: "Excel (download/upload through UI)", disabled: false },
  { value: "PDF (Via Email)", disabled: true },
  { value: "IDOC", disabled: true },
];
const ingestionFieldTypeOptions = [
  "Chain of custody",
  "First Choice",
  "Second Choice",
];
const ingestionValueOptions = ["Mandatory", "Not Mandatory", "Do not read"];

let idCounter = 0;
const genId = () => `ing_${Date.now()}_${++idCounter}`;

const createNewColumn = () => ({
  id: genId(),
  columnName: "",
  fieldType: "",
  dataElement: "",
  dataElementId: null,
  dataElementLabel: "",
  ingestion: "",
  ingestionConfigColumnId: null,
  markForDelete: false,
  columnNameError: false,
});

const createNewTab = () => ({
  id: genId(),
  tabName: "",
  columns: [createNewColumn()],
});

const createNewIngestionSection = () => ({
  id: genId(),
  category: "",
  ingestThrough: "",
  fileHasMultipleTabs: "Yes",
  tabs: [createNewTab()],
});

// Helper function to map API ingestionConfigurations to UI format
const mapIngestionConfigurationsFromApi = (apiConfigurations) => {
  if (!apiConfigurations || !Array.isArray(apiConfigurations)) {
    return [createNewIngestionSection()];
  }

  return apiConfigurations.map((section) => ({
    id: section.id || genId(),
    ingestionConfigId: section.ingestionConfigId,
    category: section.category || "",
    ingestThrough: section.ingestThrough || "",
    fileHasMultipleTabs: typeof section.fileHasMultipleTabs === "boolean" 
      ? (section.fileHasMultipleTabs ? "Yes" : "No")
      : section.fileHasMultipleTabs || "Yes",
    // API returns markForDelete as a string ("false"/"true")
    markForDelete: section.markForDelete === true || section.markForDelete === "true",
    tabs: section.columnMappings && Array.isArray(section.columnMappings) 
      ? Object.values(
          section.columnMappings.reduce((acc, col) => {
            const tabName = col.tabName || "";
            if (!acc[tabName]) {
              acc[tabName] = {
                id: genId(),
                tabName: tabName,
                columns: []
              };
            }
            acc[tabName].columns.push({
              id: col.id || genId(),
              columnName: col.columnName || "",
              fieldType: col.fieldType || "",
              dataElement: col.dataElement || "",
              dataElementId: col.dataElementId || null,
              dataElementLabel: col.dataElementLabel || col.dataElement || "",
              ingestion: col.ingestion || "",
              ingestionConfigColumnId: col.ingestionConfigColumnId || null,
              markForDelete: col.markForDelete || false,
            });
            return acc;
          }, {})
        )
      : section.tabs && Array.isArray(section.tabs)
      ? section.tabs.map((tab) => ({
          id: tab.id || genId(),
          tabName: tab.tabName || "",
          columns: tab.columns && Array.isArray(tab.columns)
            ? tab.columns.map((col) => ({
                id: col.id || genId(),
                columnName: col.columnName || "",
                fieldType: col.fieldType || "",
                dataElement: col.dataElement || "",
                dataElementId: col.dataElementId || null,
                dataElementLabel: col.dataElementLabel || col.dataElement || "",
                ingestion: col.ingestion || "",
                ingestionConfigColumnId: col.ingestionConfigColumnId || null,
                markForDelete: col.markForDelete || false,
              }))
            : [createNewColumn()],
        }))
      : [createNewTab()],
  }));
};

const IngestionConfigurationSection = forwardRef(
  ({ initialData = {}, editMode = false, onChange, dataElements = [], isDataElementsLoading = false }, ref) => {
    const classes = useStyles();

    const [sections, setSections] = useState(() => {
      // Check for ingestionSections first (from form data)
      if (initialData?.ingestionSections && Array.isArray(initialData.ingestionSections) && initialData.ingestionSections.length > 0) {
        return initialData.ingestionSections;
      }
      // Then check for ingestionConfigurations (from API response)
      if (initialData?.ingestionConfigurations && Array.isArray(initialData.ingestionConfigurations) && initialData.ingestionConfigurations.length > 0) {
        return mapIngestionConfigurationsFromApi(initialData.ingestionConfigurations);
      }
      // Default empty section
      return [createNewIngestionSection()];
    });

    const [dataElementEditState, setDataElementEditState] = useState({});
    const [dataElementEditPrev, setDataElementEditPrev] = useState({});

    useEffect(() => {
      // Check for ingestionSections first
      if (
        initialData?.ingestionSections &&
        Array.isArray(initialData.ingestionSections) &&
        initialData.ingestionSections.length > 0
      ) {
        setSections(initialData.ingestionSections);
      }
      // Then check for ingestionConfigurations from API
      else if (
        initialData?.ingestionConfigurations &&
        Array.isArray(initialData.ingestionConfigurations) &&
        initialData.ingestionConfigurations.length > 0
      ) {
        setSections(mapIngestionConfigurationsFromApi(initialData.ingestionConfigurations));
      }
    }, [initialData]);

    // Enrich sections with dataElement keys when dataElements are loaded
    useEffect(() => {
      if (!dataElements || dataElements.length === 0 || !sections || sections.length === 0) {
        return;
      }

      // Check if any column is missing dataElementId
      const needsEnrichment = sections.some(section =>
        section.tabs.some(tab =>
          tab.columns.some(col => !col.dataElementId && (col.dataElement || col.dataElementLabel))
        )
      );

      if (!needsEnrichment) return;

      // Enrich sections with dataElement keys and IDs
      const enrichedSections = sections.map(section => {
        const categoryData = dataElements.find(item => item.category === section.category);
        if (!categoryData) return section;

        return {
          ...section,
          tabs: section.tabs.map(tab => ({
            ...tab,
            columns: tab.columns.map(col => {
              // Skip if dataElementId already exists, or if both dataElement and dataElementLabel are missing
              if (col.dataElementId || (!col.dataElement && !col.dataElementLabel)) return col;
              
              // Find dataElement by key or label
              const element = categoryData.dataElements.find(
                el => el.dataElementKey === col.dataElement || el.dataElementLabel === col.dataElementLabel
              );
              
              if (element) {
                return {
                  ...col,
                  dataElement: element.dataElementKey,
                  dataElementId: element.dataElementId,
                  dataElementLabel: element.dataElementLabel,
                };
              }
              return col;
            }),
          })),
        };
      });

      setSections(enrichedSections);
    }, [dataElements, initialData?.ingestionConfigurations]);

    const notifyChange = (updatedSections) => {
      if (onChange)
        onChange({ ingestionSections: updatedSections }, true);
    };

    useImperativeHandle(ref, () => ({
      getFormData: () => ({ ingestionSections: sections }),
      getPayload: () => ({
        updateIngestionConfigurationFlag: true,
        ingestionConfigurations: sections.map((section) => ({
          ingestionConfigId: section.ingestionConfigId || null,
          category: section.category,
          ingestThrough: section.ingestThrough,
          fileHasMultipleTabs: section.fileHasMultipleTabs === "Yes",
          markForDelete: section.markForDelete || false,
          columnMappings: section.tabs.flatMap((tab) => 
            tab.columns.map((col) => ({
              tabName: tab.tabName || "",
              columnName: col.columnName,
              dataElement: col.dataElement,
              dataElementId: col.dataElementId || null,
              fieldType: col.fieldType,
              ingestion: col.ingestion,
              ingestionConfigColumnId: col.ingestionConfigColumnId || null,
              markForDelete: col.markForDelete || false,
            }))
          ),
        })),
      }),
      validateForm: () => {
        // Check if all sections are complete
        return sections.some((s) => !s.markForDelete) && sections.every(isSectionComplete);
      },
      isFormValid: () => {
        // Check if all sections are complete
        return sections.some((s) => !s.markForDelete) && sections.every(isSectionComplete);
      },
      resetForm: () => {
        setSections(
          initialData?.ingestionSections &&
            Array.isArray(initialData.ingestionSections) &&
            initialData.ingestionSections.length > 0
            ? initialData.ingestionSections
            : [createNewIngestionSection()]
        );
        setDataElementEditState({});
        setDataElementEditPrev({});
      },
    }));

    const handleSectionFieldChange = (sectionId, field, value) => {
      setSections((prev) => {
        const updated = prev.map((section) => {
          if (section.id !== sectionId) return section;
          const s = { ...section, [field]: value };
          if (field === "fileHasMultipleTabs" && value === "No") {
            s.tabs = [{ ...s.tabs[0], tabName: "" }];
          }
          return s;
        });
        notifyChange(updated);
        return updated;
      });
    };

    const handleTabNameChange = (sectionId, tabId, value) => {
      setSections((prev) =>
        prev.map((section) => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            tabs: section.tabs.map((tab) =>
              tab.id === tabId ? { ...tab, tabName: value } : tab
            ),
          };
        })
      );
    };

    const handleTabNameBlur = (sectionId, tabId) => {
      setSections((prev) => {
        const updated = prev.map((section) => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            tabs: section.tabs.map((tab) =>
              tab.id === tabId ? { ...tab, tabName: tab.tabName.trim() } : tab
            ),
          };
        });
        notifyChange(updated);
        return updated;
      });
    };

    const validateColumnNamesInTab = (tab) => {
      const activeColumns = tab.columns.filter(col => !col.markForDelete);
      const columnNameCounts = {};
      activeColumns.forEach(col => {
        const trimmedName = col.columnName.trim().toLowerCase();
        if (trimmedName) {
          columnNameCounts[trimmedName] = (columnNameCounts[trimmedName] || 0) + 1;
        }
      });
      return activeColumns.map(col => {
        const trimmedName = col.columnName.trim().toLowerCase();
        return {
          ...col,
          columnNameError: trimmedName && columnNameCounts[trimmedName] > 1
        };
      });
    };

    const handleColumnChange = (
      sectionId,
      tabId,
      columnId,
      field,
      value
    ) => {
      setSections((prev) => {
        const updated = prev.map((section) => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            tabs: section.tabs.map((tab) => {
              if (tab.id !== tabId) return tab;
              let updatedColumns = tab.columns.map((col) => {
                if (col.id !== columnId) return col;
                const updatedCol = { ...col, [field]: value };
                if (
                  field === "fieldType" &&
                  value === "Chain of custody"
                ) {
                  updatedCol.ingestion = "Mandatory";
                }
                if (
                  field === "fieldType" &&
                  col.fieldType === "Chain of custody" &&
                  value !== "Chain of custody"
                ) {
                  updatedCol.ingestion = "";
                }
                return updatedCol;
              });
              // Validate column names if columnName field was changed
              if (field === "columnName") {
                updatedColumns = validateColumnNamesInTab({ ...tab, columns: updatedColumns });
              }
              return {
                ...tab,
                columns: updatedColumns,
              };
            }),
          };
        });
        // For dropdown changes, notify immediately
        if (field !== "columnName") {
          notifyChange(updated);
        }
        return updated;
      });
    };

    const handleDataElementChange = (sectionId, tabId, columnId, dataElementId, dataElementKey, dataElementLabel) => {
      setSections((prev) => {
        const updated = prev.map((section) => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            tabs: section.tabs.map((tab) => {
              if (tab.id !== tabId) return tab;
              return {
                ...tab,
                columns: tab.columns.map((col) => {
                  if (col.id !== columnId) return col;
                  return {
                    ...col,
                    dataElementId: dataElementId,
                    dataElement: dataElementKey,
                    dataElementLabel: dataElementLabel,
                  };
                }),
              };
            }),
          };
        });
        notifyChange(updated);
        return updated;
      });
    };

    const handleColumnNameBlur = (sectionId, tabId, columnId) => {
      setSections((prev) => {
        const updated = prev.map((section) => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            tabs: section.tabs.map((tab) => {
              if (tab.id !== tabId) return tab;
              let updatedColumns = tab.columns.map((col) => {
                if (col.id !== columnId) return col;
                return { ...col, columnName: col.columnName.trim() };
              });
              // Validate column names on blur
              updatedColumns = validateColumnNamesInTab({ ...tab, columns: updatedColumns });
              return {
                ...tab,
                columns: updatedColumns,
              };
            }),
          };
        });
        notifyChange(updated);
        return updated;
      });
    };

    const addColumn = (sectionId, tabId) => {
      setSections((prev) => {
        const updated = prev.map((section) => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            tabs: section.tabs.map((tab) => {
              if (tab.id !== tabId) return tab;
              return {
                ...tab,
                columns: [...tab.columns, createNewColumn()],
              };
            }),
          };
        });
        notifyChange(updated);
        return updated;
      });
    };

    const removeColumn = (sectionId, tabId, columnId) => {
      // Clean up edit state for the deleted column
      setDataElementEditState((prev) => {
        const newState = { ...prev };
        delete newState[columnId];
        return newState;
      });
      setDataElementEditPrev((prev) => {
        const newPrev = { ...prev };
        delete newPrev[columnId];
        return newPrev;
      });

      setSections((prev) => {
        const updated = prev.map((section) => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            tabs: section.tabs.map((tab) => {
              if (tab.id !== tabId) return tab;
              if (tab.columns.length <= 1) return tab;
              return {
                ...tab,
                // Saved columns are kept and flagged so the backend can delete them; unsaved ones are dropped
                columns: tab.columns
                  .filter((col) => col.id !== columnId || col.ingestionConfigColumnId)
                  .map((col) =>
                    col.id === columnId ? { ...col, markForDelete: true } : col
                  ),
              };
            }),
          };
        });
        notifyChange(updated);
        return updated;
      });
    };

    const addTab = (sectionId) => {
      setSections((prev) => {
        const updated = prev.map((section) => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            tabs: [...section.tabs, createNewTab()],
          };
        });
        notifyChange(updated);
        return updated;
      });
    };

    const removeTab = (sectionId, tabId) => {
      setSections((prev) => {
        // Clean up edit state for all columns in the deleted tab
        const section = prev.find((s) => s.id === sectionId);
        const tab = section?.tabs.find((t) => t.id === tabId);
        if (tab) {
          setDataElementEditState((prevState) => {
            const newState = { ...prevState };
            tab.columns.forEach((col) => {
              delete newState[col.id];
            });
            return newState;
          });
          setDataElementEditPrev((prevState) => {
            const newPrev = { ...prevState };
            tab.columns.forEach((col) => {
              delete newPrev[col.id];
            });
            return newPrev;
          });
        }

        const updated = prev.map((section) => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            tabs: section.tabs
              .map((tab) => {
                if (tab.id !== tabId) return tab;
                // Flag saved columns for deletion; unsaved ones are dropped
                return {
                  ...tab,
                  columns: tab.columns
                    .filter((col) => col.ingestionConfigColumnId)
                    .map((col) => ({ ...col, markForDelete: true })),
                };
              })
              .filter((tab) => tab.columns.length > 0),
          };
        });
        notifyChange(updated);
        return updated;
      });
    };

    const addSection = () => {
      setSections((prev) => {
        const updated = [...prev, createNewIngestionSection()];
        notifyChange(updated);
        return updated;
      });
    };

    const removeSection = (sectionId) => {
      setSections((prev) => {
        const sectionToRemove = prev.find((s) => s.id === sectionId);
        
        // Clean up edit state for all columns in the deleted section
        if (sectionToRemove) {
          setDataElementEditState((prevState) => {
            const newState = { ...prevState };
            sectionToRemove.tabs.forEach((tab) => {
              tab.columns.forEach((col) => {
                delete newState[col.id];
              });
            });
            return newState;
          });
          setDataElementEditPrev((prevState) => {
            const newPrev = { ...prevState };
            sectionToRemove.tabs.forEach((tab) => {
              tab.columns.forEach((col) => {
                delete newPrev[col.id];
              });
            });
            return newPrev;
          });
        }

        // Saved sections are kept and flagged so the backend can delete them; unsaved ones are dropped
        const updated = sectionToRemove?.ingestionConfigId
          ? prev.map((s) => (s.id === sectionId ? { ...s, markForDelete: true } : s))
          : prev.filter((s) => s.id !== sectionId);
        notifyChange(updated);
        return updated;
      });
    };

    const handleDataElementEditClick = (columnId, col) => {
      setDataElementEditPrev((prev) => ({
        ...prev,
        [columnId]: {
          dataElementId: col.dataElementId || null,
          dataElement: col.dataElement || "",
          dataElementLabel: col.dataElementLabel || "",
        },
      }));
      setDataElementEditState((prev) => ({ ...prev, [columnId]: true }));
    };

    const handleDataElementSave = (columnId) => {
      setDataElementEditState((prev) => ({ ...prev, [columnId]: false }));
    };

    const handleDataElementCancel = (sectionId, tabId, columnId) => {
      const prevValue = dataElementEditPrev[columnId];
      if (prevValue) {
        handleDataElementChange(
          sectionId,
          tabId,
          columnId,
          prevValue.dataElementId,
          prevValue.dataElement,
          prevValue.dataElementLabel
        );
      }
      setDataElementEditState((prev) => ({ ...prev, [columnId]: false }));
    };

    const isAnyDataElementBeingEdited = () => {
      return Object.values(dataElementEditState).some(value => value === true);
    };

    const getAvailableDataElements = (section, tab, column) => {
      // Find data elements for the current category from API data
      const categoryData = dataElements.find(
        (item) => item.category === section.category
      );
      const allElements = categoryData?.dataElements || [];
      
      if (column.fieldType === "Chain of custody") {
        // Chain of custody: Only disable elements from Chain of custody in SAME tab (not other tabs)
        const selectedByChainInSameTab = tab.columns
          .filter((col) => !col.markForDelete && col.id !== column.id && col.fieldType === "Chain of custody")
          .map((col) => col.dataElementId)
          .filter(Boolean);
        
        return allElements
          .filter((el) => !el.markForDelete)
          .map((el) => ({
            value: el.dataElementId,
            label: el.dataElementLabel,
            dataElementKey: el.dataElementKey,
            dataElementId: el.dataElementId,
            disabled: selectedByChainInSameTab.includes(el.dataElementId),
          }));
      }
      
      if (column.fieldType === "First Choice") {
        // First Choice: Disable if used by:
        // 1. Other First Choice in same tab
        // 2. Chain of Custody in same tab
        // 3. First Choice in other tabs (from all sections with same category)
        // BUT can use Chain of Custody from other tabs
        
        const selectedByFirstChoiceInSameTab = tab.columns
          .filter((col) => !col.markForDelete && col.id !== column.id && col.fieldType === "First Choice")
          .map((col) => col.dataElementId)
          .filter(Boolean);
        
        const selectedByChainInSameTab = tab.columns
          .filter((col) => !col.markForDelete && col.fieldType === "Chain of custody")
          .map((col) => col.dataElementId)
          .filter(Boolean);
        
        const selectedByFirstChoiceInOtherTabs = sections
          .filter((s) => !s.markForDelete && s.category === section.category) // All sections with same category
          .flatMap((s) => s.tabs)
          .filter((t) => t.id !== tab.id) // Exclude current tab
          .flatMap((t) => t.columns)
          .filter((col) => !col.markForDelete && col.fieldType === "First Choice")
          .map((col) => col.dataElementId)
          .filter(Boolean);
        
        const disabledElements = [...selectedByFirstChoiceInSameTab, ...selectedByChainInSameTab, ...selectedByFirstChoiceInOtherTabs];
        
        return allElements
          .filter((el) => !el.markForDelete)
          .map((el) => ({
            value: el.dataElementId,
            label: el.dataElementLabel,
            dataElementKey: el.dataElementKey,
            dataElementId: el.dataElementId,
            disabled: disabledElements.includes(el.dataElementId),
          }));
      }
      
      if (column.fieldType === "Second Choice") {
        // Second Choice: Show elements selected by First Choice OR Chain of Custody in the same section

        // Get all First Choice elements from the same section
        const selectedByFirstChoiceInSameSection = section.tabs
          .flatMap((t) => t.columns)
          .filter((col) => !col.markForDelete && col.fieldType === "First Choice")
          .map((col) => col.dataElementId)
          .filter(Boolean);

        // Get Chain of Custody elements from the same section
        const selectedByChainInSameSection = section.tabs
          .flatMap((t) => t.columns)
          .filter((col) => !col.markForDelete && col.fieldType === "Chain of custody")
          .map((col) => col.dataElementId)
          .filter(Boolean);

        // Combined pool: First Choice + Chain of Custody from same section
        const availablePool = [...new Set([...selectedByFirstChoiceInSameSection, ...selectedByChainInSameSection])];

        // Disable elements already picked by other Second Choice columns in the same tab
        const selectedBySecondChoiceInSameTab = tab.columns
          .filter((col) => !col.markForDelete && col.id !== column.id && col.fieldType === "Second Choice")
          .map((col) => col.dataElementId)
          .filter(Boolean);

        // Filter to show ONLY First Choice and Chain of Custody elements from same section
        return allElements
          .filter((el) => !el.markForDelete)
          .filter((el) => availablePool.includes(el.dataElementId))
          .map((el) => ({
            value: el.dataElementId,
            label: el.dataElementLabel,
            dataElementKey: el.dataElementKey,
            dataElementId: el.dataElementId,
            disabled: selectedBySecondChoiceInSameTab.includes(el.dataElementId),
          }));
      }
      
      // Default: return all elements
      return allElements
        .filter((el) => !el.markForDelete)
        .map((el) => ({
          value: el.dataElementId,
          label: el.dataElementLabel,
          dataElementKey: el.dataElementKey,
          dataElementId: el.dataElementId,
          disabled: false,
        }));
    };

    const isChainOfCustodyUsed = (tab, currentColumnId) => {
      return tab.columns.some(
        (col) =>
          col.id !== currentColumnId &&
          col.fieldType === "Chain of custody"
      );
    };

    const isSectionComplete = (section) => {
      if (section.markForDelete) return true;
      if (!section.category || !section.ingestThrough) return false;
      // Filter out tabs where all columns are deleted
      const activeTabs = section.tabs.filter((tab) => tab.columns.some((col) => !col.markForDelete));
      return activeTabs.every((tab) => {
        if (
          section.fileHasMultipleTabs === "Yes" &&
          !tab.tabName.trim()
        )
          return false;
        return tab.columns
          .filter((col) => !col.markForDelete)
          .every(
            (col) =>
              col.columnName.trim() &&
              col.fieldType &&
              col.dataElementId &&
              col.ingestion &&
              !col.columnNameError
          );
      });
    };

    const isTabComplete = (section, tab) => {
      if (
        section.fileHasMultipleTabs === "Yes" &&
        !tab.tabName.trim()
      )
        return false;
      return tab.columns
        .filter((col) => !col.markForDelete)
        .every(
          (col) =>
            col.columnName.trim() &&
            col.fieldType &&
            col.dataElementId &&
            col.ingestion &&
            !col.columnNameError
        );
    };

    const readOnly = !editMode;

    return (
      <div className="cmo-stepper-wrapper shipment-config-step">
        <div className="stepper-container">
          <div>
            {sections.map((section) => {
              if (section.markForDelete) return null;
              const showTabColumn =
                section.fileHasMultipleTabs === "Yes";
              return (
                <div key={section.id} style={{ marginBottom: 40 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl className={classes.formField}>
                        <Typography component="label" style={{ fontWeight: 700, margin: "0 0 8px 0", lineHeight: "24px", display: "block" }}>
                          Category{" "}
                          {!readOnly && (
                            <span style={{ color: "red" }}>*</span>
                          )}
                        </Typography>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {readOnly ? (
                            <Typography>
                              {section.category || "N/A"}
                            </Typography>
                          ) : (
                            <Select
                              value={section.category}
                              onChange={(e) =>
                                handleSectionFieldChange(
                                  section.id,
                                  "category",
                                  e.target.value
                                )
                              }
                              variant="outlined"
                              fullWidth
                              displayEmpty
                              style={commonInlineStyles.input}
                              inputProps={commonInlineStyles.inputProps}
                            >
                              <MenuItem value="" disabled>
                                Select from the list
                              </MenuItem>
                              {ingestionCategoryOptions.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                          {!readOnly && sections.filter((s) => !s.markForDelete).length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                removeSection(section.id)
                              }
                              style={{ marginLeft: 4 }}
                            >
                              <DeleteOutlineIcon
                                fontSize="small"
                                style={{ color: "#d32f2f" }}
                              />
                            </IconButton>
                          )}
                        </div>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl className={classes.formField}>
                        <Typography component="label" style={{ fontWeight: 700, margin: "0 0 8px 0", lineHeight: "24px", display: "block" }}>
                          Ingest through{" "}
                          {!readOnly && (
                            <span style={{ color: "red" }}>*</span>
                          )}
                        </Typography>
                        {readOnly ? (
                          <Typography>
                            {section.ingestThrough || "N/A"}
                          </Typography>
                        ) : (
                          <Select
                            value={section.ingestThrough}
                            onChange={(e) =>
                              handleSectionFieldChange(
                                section.id,
                                "ingestThrough",
                                e.target.value
                              )
                            }
                            variant="outlined"
                            fullWidth
                            displayEmpty
                            style={commonInlineStyles.input}
                            inputProps={commonInlineStyles.inputProps}
                          >
                            <MenuItem value="" disabled>
                              Select from the list
                            </MenuItem>
                            {ingestThroughOptions.map((opt) => (
                              <MenuItem
                                key={opt.value}
                                value={opt.value}
                                disabled={opt.disabled}
                              >
                                {opt.value}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl className={classes.formField}>
                        <Typography component="label" style={{ fontWeight: 700, margin: "0 0 8px 0", lineHeight: "24px", display: "block" }}>
                          File has multiple tabs{" "}
                          {!readOnly && (
                            <span style={{ color: "red" }}>*</span>
                          )}
                        </Typography>
                        {readOnly ? (
                          <Typography>
                            {section.fileHasMultipleTabs || "N/A"}
                          </Typography>
                        ) : (
                          <Select
                            value={section.fileHasMultipleTabs}
                            onChange={(e) =>
                              handleSectionFieldChange(
                                section.id,
                                "fileHasMultipleTabs",
                                e.target.value
                              )
                            }
                            variant="outlined"
                            fullWidth
                            displayEmpty
                            style={commonInlineStyles.input}
                            inputProps={commonInlineStyles.inputProps}
                          >
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                          </Select>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>

                  <TableContainer
                    component={Paper}
                    className={classes.table}
                  >
                    <Table>
                      <TableHead className={classes.tableHead}>
                        <TableRow>
                          {showTabColumn && (
                            <TableCell
                              className={classes.tableCell}
                              style={{ width: "20%" }}
                            >
                              <strong>
                                Tab Name{" "}
                                <span style={{ color: "red" }}>
                                  *
                                </span>
                              </strong>
                            </TableCell>
                          )}
                          <TableCell
                            className={classes.tableCell}
                            style={{ width: "22%" }}
                          >
                            <strong>
                              Column Name{" "}
                              <span style={{ color: "red" }}>*</span>
                            </strong>
                          </TableCell>
                          <TableCell
                            className={classes.tableCell}
                            style={{ width: "16%" }}
                          >
                            <strong>
                              Field Type{" "}
                              <span style={{ color: "red" }}>*</span>
                            </strong>
                          </TableCell>
                          <TableCell
                            className={classes.tableCell}
                            style={{ width: "24%" }}
                          >
                            <strong>
                              Data Elements{" "}
                              <span style={{ color: "red" }}>*</span>
                            </strong>
                          </TableCell>
                          <TableCell
                            className={classes.tableCell}
                            style={{ width: "18%" }}
                          >
                            <strong>
                              Ingestion{" "}
                              <span style={{ color: "red" }}>*</span>
                            </strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {section.tabs
                          .filter((tab) => tab.columns.some((col) => !col.markForDelete))
                          .map((tab, tabIndex) => (
                          <React.Fragment key={tab.id}>
                            {tab.columns.filter((col) => !col.markForDelete).map((col, colIndex) => (
                              <TableRow key={col.id}>
                                {showTabColumn &&
                                  colIndex === 0 && (
                                    <TableCell
                                      rowSpan={tab.columns.filter((col) => !col.markForDelete).length + (!readOnly ? 1 : 0)}
                                      style={{
                                        verticalAlign: "top",
                                      }}
                                    >
                                      {readOnly ? (
                                        <Typography>
                                          {tab.tabName || "N/A"}
                                        </Typography>
                                      ) : (
                                        <>
                                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            <TextField
                                              size="small"
                                              variant="outlined"
                                              value={tab.tabName}
                                              onChange={(e) =>
                                                handleTabNameChange(
                                                  section.id,
                                                  tab.id,
                                                  e.target.value
                                                )
                                              }
                                              onBlur={() =>
                                                handleTabNameBlur(
                                                  section.id,
                                                  tab.id
                                                )
                                              }
                                              placeholder="Enter tab name"
                                              inputProps={{
                                                style: {
                                                  fontSize: "14px",
                                                },
                                              }}
                                              style={{ flex: 1 }}
                                              disabled={isAnyDataElementBeingEdited()}
                                            />
                                            {section.tabs.filter((t) => t.columns.some((col) => !col.markForDelete)).length > 1 && (
                                              <IconButton
                                                size="small"
                                                onClick={() => removeTab(section.id, tab.id)}
                                                style={{ padding: 4 }}
                                                disabled={isAnyDataElementBeingEdited()}
                                              >
                                                <DeleteOutlineIcon
                                                  fontSize="small"
                                                  style={{ color: "#d32f2f" }}
                                                />
                                              </IconButton>
                                            )}
                                          </div>
                                          <Typography
                                            component="span"
                                            onClick={() => {
                                              if (
                                                !isTabComplete(
                                                  section,
                                                  tab
                                                ) ||
                                                isAnyDataElementBeingEdited()
                                              )
                                                return;
                                              addTab(section.id);
                                            }}
                                            style={{
                                              color: (isTabComplete(
                                                section,
                                                tab
                                              ) && !isAnyDataElementBeingEdited())
                                                ? "#ec6535"
                                                : "#ccc",
                                              cursor: (isTabComplete(
                                                section,
                                                tab
                                              ) && !isAnyDataElementBeingEdited())
                                                ? "pointer"
                                                : "not-allowed",
                                              marginTop: 8,
                                              fontSize: 14,
                                              display: "block",
                                            }}
                                          >
                                            + Add tab
                                          </Typography>
                                        </>
                                      )}
                                    </TableCell>
                                  )}
                                <TableCell>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {readOnly ? (
                                      <Typography>
                                        {col.columnName || "N/A"}
                                      </Typography>
                                    ) : (
                                      <>
                                        <TextField
                                          size="small"
                                          variant="outlined"
                                          value={col.columnName}
                                          onChange={(e) =>
                                            handleColumnChange(
                                              section.id,
                                              tab.id,
                                              col.id,
                                              "columnName",
                                              e.target.value
                                            )
                                          }
                                          onBlur={() =>
                                            handleColumnNameBlur(
                                              section.id,
                                              tab.id,
                                              col.id
                                            )
                                          }
                                          placeholder="Enter column name"
                                          inputProps={{
                                            style: {
                                              fontSize: "14px",
                                            },
                                          }}
                                          style={{ flexGrow: 1 }}
                                          disabled={isAnyDataElementBeingEdited()}
                                          error={col.columnNameError}
                                          helperText={col.columnNameError ? "Column name already exists in this tab" : ""}
                                        />
                                        {tab.columns.length > 1 && (
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              removeColumn(
                                                section.id,
                                                tab.id,
                                                col.id
                                              )
                                            }
                                          >
                                            <DeleteOutlineIcon
                                              fontSize="small"
                                              style={{
                                                color: "#d32f2f",
                                              }}
                                            />
                                          </IconButton>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={
                                      classes.mandatoryDropdown
                                    }
                                  >
                                    <Select
                                      value={col.fieldType}
                                      onChange={(e) =>
                                        handleColumnChange(
                                          section.id,
                                          tab.id,
                                          col.id,
                                          "fieldType",
                                          e.target.value
                                        )
                                      }
                                      disabled={readOnly || isAnyDataElementBeingEdited()}
                                      displayEmpty
                                    >
                                      <MenuItem value="" disabled>
                                        Select
                                      </MenuItem>
                                      {ingestionFieldTypeOptions.map(
                                        (opt) => (
                                          <MenuItem
                                            key={opt}
                                            value={opt}
                                            disabled={
                                              opt ===
                                                "Chain of custody" &&
                                              isChainOfCustodyUsed(
                                                tab,
                                                col.id
                                              )
                                            }
                                          >
                                            {opt}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  {dataElementEditState[col.id] ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {isDataElementsLoading ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        <Autocomplete
                                          options={getAvailableDataElements(
                                            section,
                                            tab,
                                            col
                                          )}
                                          getOptionLabel={(option) =>
                                            typeof option === "string"
                                              ? option
                                              : option.label || option.value || ""
                                          }
                                          getOptionDisabled={(
                                            option
                                          ) => option.disabled}
                                          value={
                                            col.dataElementId
                                              ? getAvailableDataElements(section, tab, col).find(
                                                  (opt) => opt.value === col.dataElementId
                                                ) || null
                                              : null
                                          }
                                          onChange={(e, newValue) => {
                                            if (newValue) {
                                              // Save id, key, and label in a single update
                                              handleDataElementChange(
                                                section.id,
                                                tab.id,
                                                col.id,
                                                newValue.dataElementId,
                                                newValue.dataElementKey,
                                                newValue.label
                                              );
                                            } else {
                                              handleDataElementChange(
                                                section.id,
                                                tab.id,
                                                col.id,
                                                null,
                                                "",
                                                ""
                                              );
                                            }
                                          }}
                                          getOptionSelected={(
                                            option,
                                            value
                                          ) =>
                                            option.value === value.value
                                          }
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              variant="outlined"
                                              size="small"
                                              placeholder="Search..."
                                            />
                                          )}
                                          style={{ minWidth: 200, flexGrow: 1 }}
                                          disableClearable
                                          openOnFocus
                                        />
                                      )}
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleDataElementSave(
                                            col.id
                                          )
                                        }
                                        style={{ color: "green" }}
                                      >
                                        <Check fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleDataElementCancel(
                                            section.id,
                                            tab.id,
                                            col.id
                                          )
                                        }
                                        style={{ color: "#d32f2f" }}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <span
                                        style={{ marginRight: 4, flexGrow: 1 }}
                                      >
                                        {col.dataElementLabel || col.dataElement || "N/A"}
                                      </span>
                                      {!readOnly && (
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleDataElementEditClick(
                                              col.id,
                                              col
                                            )
                                          }
                                          disabled={isAnyDataElementBeingEdited() || !col.fieldType}
                                        >
                                          <EditIcon
                                            fontSize="small"
                                            style={{
                                              color: (isAnyDataElementBeingEdited() || !col.fieldType) ? "#ccc" : "#ec6535",
                                            }}
                                          />
                                        </IconButton>
                                      )}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={
                                      classes.mandatoryDropdown
                                    }
                                  >
                                    <Select
                                      value={col.ingestion}
                                      onChange={(e) =>
                                        handleColumnChange(
                                          section.id,
                                          tab.id,
                                          col.id,
                                          "ingestion",
                                          e.target.value
                                        )
                                      }
                                      disabled={
                                        readOnly ||
                                        col.fieldType ===
                                          "Chain of custody" ||
                                        isAnyDataElementBeingEdited()
                                      }
                                      displayEmpty
                                    >
                                      <MenuItem value="" disabled>
                                        Select
                                      </MenuItem>
                                      {ingestionValueOptions.map(
                                        (opt) => (
                                          <MenuItem
                                            key={opt}
                                            value={opt}
                                          >
                                            {opt}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                  </FormControl>
                                </TableCell>
                              </TableRow>
                            ))}
                            {!readOnly && (
                              <TableRow>
                                <TableCell colSpan={4}>
                                  <Typography
                                    component="span"
                                    onClick={() => {
                                      if (
                                        !isTabComplete(section, tab) ||
                                        isAnyDataElementBeingEdited()
                                      )
                                        return;
                                      addColumn(section.id, tab.id);
                                    }}
                                    style={{
                                      color: (isTabComplete(
                                        section,
                                        tab
                                      ) && !isAnyDataElementBeingEdited())
                                        ? "#ec6535"
                                        : "#ccc",
                                      cursor: (isTabComplete(
                                        section,
                                        tab
                                      ) && !isAnyDataElementBeingEdited())
                                        ? "pointer"
                                        : "not-allowed",
                                      fontSize: 14,
                                    }}
                                  >
                                    + Add column
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              );
            })}
            {!readOnly && (
              <Button
                variant="contained"
                onClick={() => {
                  if (!sections.every(isSectionComplete)) return;
                  addSection();
                }}
                disabled={!sections.every(isSectionComplete)}
                style={{
                  backgroundColor: sections.every(isSectionComplete)
                    ? "#ec6535"
                    : "#ccc",
                  color: "#fff",
                  borderRadius: 6,
                  marginTop: 16,
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                + Add new section
              </Button>
            )}
          </div>
          <Divider
            style={{
              margin: "80px 0px 0px 0px",
              backgroundColor: "#707070",
              height: 1,
            }}
          />
        </div>
      </div>
    );
  }
);

export default IngestionConfigurationSection;
